import { Schema } from "effect"
import { Actor, Principal } from "./common"
import { LoadedDocument } from "./documents"
import { Artifact } from "./domain/knowledge"
import { GitSha } from "./github"
import {
  AgentId,
  ArtifactId,
  CommandId,
  ItemId,
  Millis,
  PosInt,
  ProjectId,
  UserId,
  WorkRequestId,
} from "./ids"

const Delivery = Schema.TaggedUnion({ pending: {}, delivered: {}, blocked: { reason: Schema.String } })

/** Destination facts come from authorized discovery; they never grant access by themselves. */
export const WorkPeer = Schema.Struct({
  projectId: ProjectId,
  projectName: Schema.String,
  agentId: AgentId,
  agentName: Schema.String,
  handle: Schema.String,
  role: Schema.Literals(["lead", "role"]),
  title: Schema.String,
  executionPrincipal: UserId,
})
export const WorkRequestDraft = Schema.Struct({
  id: WorkRequestId,
  target: WorkPeer,
  title: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  body: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(40_000)),
  itemId: Schema.NullOr(ItemId),
  knowledge: Schema.Array(LoadedDocument),
  intentKey: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(200)),
  expectedRevision: Schema.optionalKey(PosInt),
})
export const WorkAssignment = Schema.Struct({
  revision: PosInt,
  target: WorkPeer,
  requester: UserId,
  authorizedBy: Schema.optionalKey(Actor),
  note: Schema.String,
  at: Millis,
  delivery: Delivery,
})
export type WorkAssignment = typeof WorkAssignment.Type
export const WorkArtifact = Schema.Struct({
  projectId: ProjectId,
  artifact: Artifact,
  revision: PosInt,
  url: Schema.String.check(Schema.isPattern(/^https?:\/\/[^\s/]+\S*$/)),
  document: Schema.optionalKey(LoadedDocument),
})
export type WorkArtifact = typeof WorkArtifact.Type
export const WorkVerification = Schema.Struct({
  id: CommandId,
  replyId: CommandId,
  artifactId: ArtifactId,
  headSha: Schema.optionalKey(GitSha),
  verdict: Schema.Literals(["accepted", "changes_requested", "inconclusive"]),
  note: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(4000)),
  by: Actor,
  at: Millis,
})
export type WorkVerification = typeof WorkVerification.Type
export const WorkReply = Schema.Struct({
  id: CommandId,
  revision: PosInt,
  outcome: Schema.Literals(["completed", "partial", "blocked", "declined"]),
  summary: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(20_000)),
  links: Schema.Array(
    Schema.String.check(Schema.isMaxLength(2_000), Schema.isPattern(/^https?:\/\/[^\s/]+\S*$/)),
  ).check(Schema.isMaxLength(20)),
  artifacts: Schema.optionalKey(Schema.Array(WorkArtifact).check(Schema.isMaxLength(20))),
  by: Actor,
  at: Millis,
  tainted: Schema.Boolean,
  delivery: Delivery,
})
export const WorkRequest = Schema.Struct({
  ...WorkRequestDraft.fields,
  sourceProjectId: ProjectId,
  sourceProjectName: Schema.String,
  sourceAgentId: AgentId,
  createdBy: Actor,
  authorizedBy: Actor,
  requester: UserId,
  /** Original asking person/agent, independent of the authority authorizing a revision and its resources. */
  askedBy: Principal,
  revision: PosInt,
  createdAt: Millis,
  updatedAt: Millis,
  tainted: Schema.Boolean,
  state: Schema.Literals(["pending", "answered", "cancelled"]),
  /** Retired revisions retain their recipient and control-delivery receipt through retargeting. */
  retired: Schema.optionalKey(Schema.Array(WorkAssignment)),
  replies: Schema.Array(WorkReply),
  verifications: Schema.optionalKey(Schema.Array(WorkVerification)),
  delivery: Delivery,
})
export type WorkRequest = typeof WorkRequest.Type
export type WorkReply = typeof WorkReply.Type
export type WorkPeer = typeof WorkPeer.Type
export type WorkRequestDraft = typeof WorkRequestDraft.Type

/** Durable locators. Receivers retrieve authorized persisted records; a locator grants no authority. */
export const CoordinationInbound = Schema.TaggedUnion({
  Request: { sourceProjectId: ProjectId, requestId: WorkRequestId, revision: PosInt },
  Control: { sourceProjectId: ProjectId, requestId: WorkRequestId, revision: PosInt },
  Reply: { sourceProjectId: ProjectId, requestId: WorkRequestId, replyId: CommandId },
})
export type CoordinationInbound = typeof CoordinationInbound.Type

export const WorkProof = Schema.TaggedUnion({
  request: { request: WorkRequest },
  reply: { reply: WorkReply },
  control: { requestId: WorkRequestId, assignment: WorkAssignment },
  superseded: {},
  denied: { reason: Schema.String },
})
export type WorkProof = typeof WorkProof.Type
export const WorkDocument = Schema.TaggedUnion({
  result: {
    result: WorkArtifact,
    text: Schema.NullOr(Schema.String),
    offset: Schema.Number,
    nextOffset: Schema.NullOr(Schema.Number),
    currentPr: Schema.optionalKey(Artifact.fields.pr),
  },
  available: {
    document: LoadedDocument,
    text: Schema.String,
    offset: Schema.Number,
    nextOffset: Schema.NullOr(Schema.Number),
  },
  unavailable: { reason: Schema.String },
})
export type WorkDocument = typeof WorkDocument.Type
